
<!--
* An image field component
*
* @param {string} imageUrl - Url of the image to display.
* @param {string} text - Caption to display under thumbnail
* @param {number} progress - The progress of image upload
* @param {boolean} isProgressing - Whether image is currently uploading
* @param {boolean} showControls - Whether to show image controls
* @param {string} acceptedTypes - html 'accept' attributes, to filter input (images, audio..)
*
* @fires MsImageUpload#emitInput
* @fires MsImageUpload#emitDeleteClick
* @fires MsImageUpload#emitCancel
-->

<!--
* Emits an input event.
*
* @event MsImageUpload#emitInput
-->

<!--
* Emits a delete click event.
*
* @event MsImageUpload#emitDeleteClick
-->

<!--
* Emits a cancel click event.
*
* @event MsImageUpload#emitCancel
-->

<template>
  <div>
    <div class="file">
      <label class="file-label">
        <input
          class="file-input"
          type="file"
          :accept="acceptedTypes"
          @change="emitInput">
        <!-- Clickable image section -->
        <span class="file-cta">
            <div class="thumbnail_wrapper">
              <!-- Progress bar & cancel button -->
              <div
                class="progress-container"
                v-if="isProgressing">
                <p
                  v-if="preparingLabel"
                  class="preparing-label">{{ preparingLabel }}</p>
                <progress
                  v-else
                  class="progress"
                  :key="progress"
                  :value="progress"
                  :max="100">
                  {{ progress }}%
                </progress>
                <div class="cancel-wrapper">
                  <button
                    class="cancel"
                    @click="emitCancel">Cancel</button>
                </div>
              </div>
              <!-- Image thumbnail -->
              <div
                class="thumbnail"
                :class="{ 'thumbnail--hide': hideImage }">
                <div
                  v-if="!image"
                  class="image_thumbnail"
                  :style="{ backgroundImage: 'url(' + defaultImageUrl + ')' }">
                </div>
                <div
                  class="image_thumbnail"
                  v-if="image">
                  <ms-image-progress
                    :imageUrl="image"
                    :fallbackUrl="fallbackUrl"
                    :maxTime="maxTime"
                    :retryWait="retryWait"
                    :loadingImageSrc="loadingImageSrc"/>
                  <!-- Image controls -->
                  <div v-if="showControls">
                    <div
                      class="delete-control"
                      @click="emitDeleteClick">
                      <img src="@/assets/theme/images/secondary-bin-icon.png">
                    </div>
                    <div class="change-control">
                      <img src="@/assets/theme/images/replace-image-icon.png">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </span>
      </label>
    </div>
  </div>
</template>

<script>
  import MsImageProgress from '@/components/ms-image-progress'
  export default {
    name: 'MsFileField',
    components: {
      MsImageProgress
    },
    props: {
      imageUrl: {
        type: String
      },

      fallbackUrl: {
        type: String,
        default: null
      },

      preparingLabel: {
        type: String,
        default: ''
      },

      acceptedTypes: {
        type: String,
        required: false,
        default: '*' // all by default
      },

      defaultImageUrl: {
        type: String
      },

      progress: {
        type: Number
      },

      isProgressing: {
        type: Boolean,
        default: false
      },

      showControls: {
        type: Boolean,
        default: false
      },

      maxTime: {
        type: Number
      },

      retryWait: {
        type: Number
      },

      loadingImageSrc: {
        type: String
      }
    },

    computed: {
      image () {
        return this.imageUrl
      },

      /**
       * Whether to hide image
       */
      hideImage: {
        get () {
          return this.isProgressing
        }
      }
    },

    methods: {

      /**
       * Emit input event
       *
       * @param {Object} event - The event object
       */
      emitInput: function (event) {
        this.$emit('input', event)
        // removed this as it caused iOS to crash (white ipad at least) on trying
        // to upload 2nd photo from camera onwards
        // this.clearInput(event)
      },

      /**
       * Clears input file value on click to allow uploading a file
       * with the same file name as previous uploaded image
       */
      clearInput: function (event) {
        // We reset the file input's value so that the change input will
        // still fire if we upload a file with the same name
        event.target.value = ''
      },

      /**
       * Emit delete click event
       *
       * @param {Object} event - The event object
       */
      emitDeleteClick: function (event) {
        event.preventDefault()
        this.$emit('deleteClick')
      },

      /**
       * Emit cancel click event
       *
       * @param {Object} event - The event object
       */
      emitCancel: function (event) {
        event.preventDefault()
        this.$emit('cancelClick')
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../assets/sass/shared.scss";

  $image-height: $dims-image-upload-image-height;

  .file-label,
  .file-cta {
    width: 100%;
  }

  .file-cta {
    display: flex;
    flex-direction: column;
    padding: 0;
    height: auto;
    border-radius: 0px;
    background: $quaternary-brand-color;
  }

  .thumbnail_wrapper {
    width: 100%;
    height: $image-height;
  }

  .thumbnail,
  .image_thumbnail {
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    opacity: 1;
  }

  img {
      display: block;
      width: auto;
      max-height: $image-height;
      margin-left: auto;
      margin-right: auto;
  }

  .progress-container {
    width: 100%;
    position: absolute;
    text-align: center;
    display: block;
    left: 0;
    top: 50%;
    transform: translate(0, -50%);

    .progress {
      width: 80%;
      margin: 0 auto;
      border-radius: 0;
    }

    .preparing-label {
      margin: 0 auto $dims-padding-default auto;
      font-style: italic;
    }
  }

  .thumbnail--hide {
    opacity: 0;
  }

  .delete-control,
  .change-control {
    display: flex;
    position: absolute;
    width: $dims-image-upload-controls-size;
    height: $dims-image-upload-controls-size;
    background: rgba(0, 0, 0, 0.5);
    right: 0;
    padding: $dims-image-upload-controls-padding;

    img {
      width: 100%;
    }
  }

  .delete-control {
    top: 0;
  }

  .change-control {
    bottom: 0;
  }

  .cancel-wrapper {
    display: inline-block;
    margin-top: $dims-padding-default;

    .cancel {
      display: inline;
      padding: $dims-image-upload-cancel-padding;
      background: $primary-brand-color;
      border: none;
      color: white;

      &:focus {
        outline: none;
      }
    }
  }

</style>


